import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import './App.css'
import Header from './components/Header'
import Tabs from './components/Tabs'
import CardsView from './views/CardsView'
import LocationsView from './views/LocationsView'
import TitlesView from './views/TitlesView'
import BingoView from './views/BingoView'
import ArticlesView from './views/ArticlesView'
import AdminEditorView from './views/AdminEditorView'
import ScrollToTop from './components/ScrollToTop'
import { translations } from './data/translations'

const ADMIN_STORAGE_KEY = 'ms_admin_session_v1'
const ADMIN_PATH = '/editor'

function App() {
  const isDev = process.env.NODE_ENV === 'development'

  const [activeView, setActiveView] = useState(() => {
    const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/'
    const hasAdminSession =
      isDev && localStorage.getItem(ADMIN_STORAGE_KEY) === '1'

    if (normalizedPath === ADMIN_PATH) {
      return hasAdminSession ? 'editor' : 'cards'
    }

    return localStorage.getItem('activeTab') || 'cards'
  })
  const [language, setLanguage] = useState('RU')
  const [showScrollButton, setShowScrollButton] = useState(false)
  const [isArticleOpen, setIsArticleOpen] = useState(false)
  const [articleMeta, setArticleMeta] = useState(null)
  const [isAdmin, setIsAdmin] = useState(() => {
    return isDev && localStorage.getItem(ADMIN_STORAGE_KEY) === '1'
  })

  useEffect(() => {
    localStorage.setItem('activeTab', activeView)
  }, [activeView])

  useEffect(() => {
    const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/'

    if (normalizedPath !== ADMIN_PATH) {
      return
    }

    if (isDev && isAdmin) {
      if (activeView !== 'editor') {
        setActiveView('editor')
      }
      return
    }

    localStorage.removeItem(ADMIN_STORAGE_KEY)
    setIsAdmin(false)
    window.history.replaceState({}, '', '/')
    if (activeView === 'editor') {
      setActiveView('cards')
    }
  }, [activeView, isAdmin, isDev])

  useEffect(() => {
    const handleScroll = () => {
      if (!isArticleOpen) {
        setShowScrollButton(window.scrollY > window.innerHeight)
      }
    }

    setShowScrollButton(window.scrollY > window.innerHeight)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isArticleOpen])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleAdminLogin = () => {
    if (!isDev || isAdmin) {
      return
    }

    localStorage.setItem(ADMIN_STORAGE_KEY, '1')
    setIsAdmin(true)
    window.alert('Admin mode enabled (dev only)')
  }

  const handleAdminLogout = () => {
    localStorage.removeItem(ADMIN_STORAGE_KEY)
    setIsAdmin(false)

    if (activeView === 'editor') {
      setActiveView('cards')
      window.history.replaceState({}, '', '/')
    }
  }

  const handleViewChange = (view) => {
    if (view === 'editor' && (!isDev || !isAdmin)) {
      return
    }

    setActiveView(view)

    if (view === 'editor' && isDev) {
      if (window.location.pathname !== ADMIN_PATH) {
        window.history.pushState({}, '', ADMIN_PATH)
      }
    } else if (window.location.pathname === ADMIN_PATH) {
      window.history.replaceState({}, '', '/')
    }

    if (view !== 'articles') {
      setArticleMeta(null)
    }
  }

  const t = translations[language]

  return (
    <div className="App">
      <Helmet>
        <html lang={language === 'RU' ? 'ru' : 'en'} />
        <title>{articleMeta ? articleMeta.title : t.title}</title>
        <meta
          name="description"
          content={
            articleMeta
              ? articleMeta.description
              : t.metaDescription[activeView] || t.metaDescription.cards
          }
        />
        <meta
          name="keywords"
          content={t.metaKeywords[activeView] || t.metaKeywords.cards}
        />
        <meta
          property="og:title"
          content={articleMeta ? articleMeta.title : t.title}
        />
        <meta
          property="og:description"
          content={
            articleMeta
              ? articleMeta.description
              : t.metaDescription[activeView] || t.metaDescription.cards
          }
        />
        <meta
          property="og:image"
          content={
            articleMeta
              ? articleMeta.image
              : `${window.location.origin}/favicon-32x32.png`
          }
        />
        <meta
          property="og:url"
          content={articleMeta ? articleMeta.url : window.location.origin}
        />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="container">
        <Header
          language={language}
          setLanguage={setLanguage}
          isAdmin={isAdmin}
          onAdminLogin={handleAdminLogin}
          onAdminLogout={handleAdminLogout}
          showAdminControls={isDev}
        />

        {isDev && isAdmin && (
          <div className="admin-badge">
            <span>Admin mode</span>
          </div>
        )}

        <Tabs
          activeView={activeView}
          handleViewChange={handleViewChange}
          language={language}
          isAdmin={isDev && isAdmin}
        />

        {activeView === 'cards' && <CardsView language={language} />}
        {activeView === 'locations' && <LocationsView language={language} />}
        {activeView === 'titles' && <TitlesView language={language} />}
        {activeView === 'bingo' && <BingoView language={language} />}
        {activeView === 'articles' && (
          <ArticlesView
            language={language}
            setIsArticleOpen={setIsArticleOpen}
            setArticleMeta={setArticleMeta}
          />
        )}
        {activeView === 'editor' && isDev && isAdmin && (
          <AdminEditorView language={language} />
        )}

        <ScrollToTop
          show={showScrollButton && !isArticleOpen}
          onClick={scrollToTop}
        />
      </div>
    </div>
  )
}

export default App
